/**
 * IDT 看板「デザイン評価(全体)」カードの「明細」モーダル。
 *
 * 「貢献度の定性評価FB」シートを **対象月度** で絞り、その月の全メンバーの回答行を
 * シートの列そのまま (回答日 / 担当者 / チケット名 / カテゴリ / 重み / 貢献度3軸 / Good / More) で
 * **回答日の新しい順** に並べる。長文コメントが主役のためテーブルではなく、
 * 左列=担当者・回答日 / 右列=評価内容 の 2 カラム明細カードのリストで描く。左列は
 * 看板の bullet / donut / karte と同じデザイナー個人色 (`colorVarOf`) で塗り、
 * 誰の回答かを色でも追えるようにする。
 * 月セレクタの候補はシートに存在する月度 (API が返す months) をそのまま使う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT 看板の評価明細モーダル。対象月度で絞った全メンバーの定性評価FB 明細リスト
 * @graph-connects useIdtEvaluationCommentMonthDetail [calls] 対象月度の全メンバー評価明細取得
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import type { IdtEvaluationCommentMonthRow } from '../../../lib/api.js';
import { useIdtEvaluationCommentMonthDetail } from '../../../hooks/useIdtKpi.js';
import { monthLabelOf } from './periods.js';
import { DesignEvalSelect } from './DesignEvalSelect.js';
import { colorVarOf } from './shared.js';

export interface EvaluationDetailModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** クローズ要求コールバック */
  onClose: () => void;
  /** 初期表示する対象月度 ("YYYY-MM")。null ならシート内の最新月 */
  initialMonth: string | null;
}

/** 貢献度 3 軸のバッジ。値が無い軸は出さない。 @graph-connects none */
function ScoreBadge({ label, value }: { label: string; value: number | null }) {
  if (value == null) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-secondary)]">
      {label}
      <span className="font-mono font-semibold tabular-nums text-[var(--color-text-primary)]">
        {value}
      </span>
    </span>
  );
}

/** Good / More コメント 1 ブロック (色付き縦罫 + 本文)。 @graph-connects none */
function CommentBlock({ label, color, text }: { label: string; color: string; text: string }) {
  return (
    <div
      className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-2"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <span
        className="mb-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
        style={{ background: `color-mix(in srgb, ${color} 18%, transparent)`, color }}
      >
        {label}
      </span>
      <p className="whitespace-pre-wrap text-xs leading-relaxed text-[var(--color-text-primary)]">
        {text}
      </p>
    </div>
  );
}

/**
 * 1 回答ぶんの明細カード。左列=担当者 + 回答日 (並び順の軸) / 右列=評価内容。
 * 担当者名は長さがまちまちなので左列は固定幅にし、右列の本文開始位置を揃える。
 * 左列は担当者の個人色 (看板 bullet / donut / karte と同じ `colorVarOf`) で
 * 縦罫 + 淡い地色を敷く。色の無い名前 (過去メンバー外・表記揺れ) は text-muted に落ちる。
 * @graph-connects none
 */
function DetailRow({ row }: { row: IdtEvaluationCommentMonthRow }) {
  const { t } = useTranslation();
  const hasComment = row.good != null || row.more != null;
  // シートの メンバー 列は表記揺れ (大文字混在) があるため、色解決は repository と同じ小文字化で行う。
  const color = `var(${colorVarOf(row.member.trim().toLowerCase())})`;

  return (
    <li className="grid grid-cols-1 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] sm:grid-cols-[136px_minmax(0,1fr)]">
      <div
        className="flex flex-col gap-0.5 border-l-[3px] px-3 py-2.5"
        style={{
          borderColor: color,
          background: `color-mix(in srgb, ${color} 12%, transparent)`,
        }}
      >
        <span className="text-sm font-bold" style={{ color }}>
          {row.member}
        </span>
        {row.answeredAt != null && (
          <span className="font-mono text-[11px] leading-tight tabular-nums text-[var(--color-text-muted)]">
            {row.answeredAt}
          </span>
        )}
      </div>

      <div className="min-w-0 px-3 py-2.5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">
            {row.ticket ?? t('idtKpi.designEvaluation.detail.noTicket')}
          </span>
          {row.category != null && (
            <span className="rounded bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-secondary)]">
              {row.category}
            </span>
          )}
          {row.weight != null && (
            <span className="font-mono text-[11px] tabular-nums text-[var(--color-text-muted)]">
              {t('idtKpi.designEvaluation.detail.weight')} {row.weight}
            </span>
          )}
        </div>

        <div className="mt-1.5 flex flex-wrap gap-1">
          <ScoreBadge label={t('idtKpi.designEvaluation.axes.planning')} value={row.planning} />
          <ScoreBadge label={t('idtKpi.designEvaluation.axes.design')} value={row.design} />
          <ScoreBadge label={t('idtKpi.designEvaluation.axes.visual')} value={row.visual} />
        </div>

        {hasComment ? (
          <div className="mt-2 flex flex-col gap-1.5">
            {row.good != null && (
              <CommentBlock
                label={t('idtKpi.karte.good')}
                color="var(--color-success)"
                text={row.good}
              />
            )}
            {row.more != null && (
              <CommentBlock
                label={t('idtKpi.karte.more')}
                color="var(--color-warning)"
                text={row.more}
              />
            )}
          </div>
        ) : (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            {t('idtKpi.designEvaluation.detail.noComment')}
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * 評価明細モーダル。対象月度セレクタ + メンバー別の明細リストを描く。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 対象月度で絞った全メンバーの定性評価FB 明細をメンバー別リストで表示するモーダル
 * @graph-connects useIdtEvaluationCommentMonthDetail [calls] 対象月度の全メンバー評価明細取得
 */
export function EvaluationDetailModal({ open, onClose, initialMonth }: EvaluationDetailModalProps) {
  const { t } = useTranslation();
  // null = API 任せ (シート内の最新月)。セレクタで選ぶと以降はその月で固定する。
  const [month, setMonth] = useState<string | null>(initialMonth);
  const query = useIdtEvaluationCommentMonthDetail(month, open);

  const rows = query.data?.rows ?? [];
  const months = query.data?.months ?? [];
  const resolvedMonth = query.data?.month ?? month;

  // 並び順 (回答日の新しい順) は API 側で確定済みなので、ここでは組み替えない。
  // 見出しの「N名」だけ担当者の実数を数える。
  const memberCount = useMemo(() => new Set(rows.map((r) => r.member)).size, [rows]);

  const monthOptions = months.map((m) => ({ value: m, label: monthLabelOf(m) }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('idtKpi.designEvaluation.detail.title')}
      description={t('idtKpi.designEvaluation.detail.subtitle')}
      size="full"
      className="animate-bubble-in"
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('idtKpi.designEvaluation.detail.summary', {
              members: memberCount,
              count: rows.length,
            })}
          </p>
          {resolvedMonth != null && monthOptions.length > 0 && (
            <DesignEvalSelect
              options={monthOptions}
              value={resolvedMonth}
              onChange={setMonth}
              ariaLabel={t('idtKpi.designEvaluation.monthSelectLabel')}
            />
          )}
        </div>

        {query.isLoading ? (
          <p className="py-12 text-center text-sm text-[var(--color-text-muted)]">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-[var(--color-text-muted)]">
            {t('idtKpi.designEvaluation.detail.empty')}
          </p>
        ) : (
          // Modal 本体 (max-h-[85vh] overflow-y-auto) が既にスクロール領域なので、
          // ここでは入れ子スクロールを作らない。
          <ul className="flex flex-col gap-2">
            {rows.map((row, i) => (
              <DetailRow
                key={`${row.answeredAt ?? ''}-${row.member}-${row.ticket ?? ''}-${String(i)}`}
                row={row}
              />
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
